import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Target, RefreshCw } from 'lucide-react';
import { getGoogleAdsClient, getDateRange } from '@/lib/google-ads-client';
import { createGoogleAdsAIOptimizer, type BudgetRecommendation } from '@/lib/google-ads-ai-optimizer';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';
import { useGoogleAdsCompany, GOOGLE_ADS_COMPANY_OPTIONS } from '@/hooks/useGoogleAdsCompany';

export default function BudgetOptimizerPage() {
  const [recommendations, setRecommendations] = useState<BudgetRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const { company, setCompany } = useGoogleAdsCompany();

  useEffect(() => { loadRecommendations(); }, [company]);

  const loadRecommendations = async () => {
    try {
      setLoading(true);
      const { data: mappings } = await supabase
        .from('google_ads_customer_mapping')
        .select('*')
        .eq('company', company)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      if (!mappings) { toast.error(`No account for ${company}`); return; }

      const client = getGoogleAdsClient(mappings.customer_id);
      const optimizer = createGoogleAdsAIOptimizer(client);
      const report = await optimizer.generateOptimizationReport(mappings.customer_id, getDateRange(30));
      setRecommendations(report.budgetRecommendations);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8"><div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-blue-500" /><span className="ml-3">Loading...</span></div></div>;
  }

  const totalCurrent = recommendations.reduce((s, r) => s + r.currentBudget, 0);
  const totalRecommended = recommendations.reduce((s, r) => s + r.recommendedBudget, 0);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex justify-end mb-4">
        <select
          value={company}
          onChange={(e) => setCompany(e.target.value as 'hales' | 'myccs' | 'abraham')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {GOOGLE_ADS_COMPANY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <h1 className="text-3xl font-bold mb-2">Budget Optimizer</h1>
      <p className="text-gray-600 mb-8">AI-powered budget allocation recommendations</p>

      <div className="grid grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2"><span className="text-sm text-gray-600">Current Total</span><DollarSign className="h-5 w-5 text-blue-500" /></div>
          <div className="text-3xl font-bold">${totalCurrent.toFixed(2)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2"><span className="text-sm text-gray-600">Recommended</span><Target className="h-5 w-5 text-green-500" /></div>
          <div className="text-3xl font-bold text-green-600">${totalRecommended.toFixed(2)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2"><span className="text-sm text-gray-600">Change</span><TrendingUp className="h-5 w-5 text-purple-500" /></div>
          <div className={`text-3xl font-bold ${totalRecommended > totalCurrent ? 'text-green-600' : 'text-red-600'}`}>
            {totalRecommended > totalCurrent ? '+' : ''}{((totalRecommended - totalCurrent) / totalCurrent * 100).toFixed(1)}%
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Budget Recommendations ({recommendations.length})</h2></div>
        <div className="divide-y">
          {recommendations.map((rec, idx) => {
            const change = rec.recommendedBudget - rec.currentBudget;
            const changePercent = (change / rec.currentBudget) * 100;

            return (
              <div key={idx} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">{rec.campaignName}</h3>
                    <p className="text-sm text-gray-700 mb-3">{rec.reason}</p>
                    <div className="grid grid-cols-3 gap-4">
                      <div><div className="text-xs text-gray-500">Current</div><div className="text-sm font-semibold">${rec.currentBudget.toFixed(2)}</div></div>
                      <div><div className="text-xs text-gray-500">Recommended</div><div className="text-sm font-semibold text-green-600">${rec.recommendedBudget.toFixed(2)}</div></div>
                      <div><div className="text-xs text-gray-500">Change</div><div className={`text-sm font-semibold ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>{change > 0 ? '+' : ''}{changePercent.toFixed(1)}%</div></div>
                    </div>
                  </div>
                  {change > 0 ? <TrendingUp className="h-8 w-8 text-green-500" /> : <TrendingDown className="h-8 w-8 text-red-500" />}
                </div>
                <div className="text-sm text-gray-600">Expected ROAS: {rec.expectedROI.toFixed(2)}x | Confidence: {(rec.confidence * 100).toFixed(0)}%</div>
              </div>
            );
          })}
        </div>
        {recommendations.length === 0 && <div className="p-12 text-center text-gray-500"><Target className="h-12 w-12 mx-auto mb-4 text-gray-400" /><p>No budget optimizations needed</p></div>}
      </div>
    </div>
  );
}
